import React, { useState, useEffect } from 'react';
import { Badge, Button, Input, Label, Spinner } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { agregarMensaje, obtenerTicket, marcarComoDuplicado, selectTicketActual, selectEnviandoMensaje, selectLoading } from '../../slices/ticketsSlice';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { DatosSesion } from '../../Components/Common/DatosSesion';
import { getUrlPrefirmadaTicket } from '../../helpers/backend_helper';
import * as SecureStorage from '../../helpers/secure_storage';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileEncode from 'filepond-plugin-file-encode';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import './TicketDrawer.css';

// Registrar plugins de FilePond
registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginFileEncode,
  FilePondPluginFileValidateType
);

const TicketDrawer = ({ isOpen, ticket, onClose, onStatusChange, tickets = [] }) => {
  const dispatch = useDispatch();
  const [mensaje, setMensaje] = useState('');
  const [files, setFiles] = useState([]);
  const [showDuplicadoModal, setShowDuplicadoModal] = useState(false);
  const [ticketOriginalId, setTicketOriginalId] = useState('');
  const [marcandoDuplicado, setMarcandoDuplicado] = useState(false);
  const [busquedaDuplicado, setBusquedaDuplicado] = useState('');
  const ticketActual = useSelector(selectTicketActual);
  const enviandoMensaje = useSelector(selectEnviandoMensaje);
  const loading = useSelector(selectLoading);
  const userData = DatosSesion('debug');
  const isAdmin = userData?.perfil === 'Administrador';
  
  // Función para formatear fecha a hora de Venezuela (UTC-4)
  const formatVenezuelaDate = (dateString) => {
    // Agregar 'Z' si no tiene timezone para que JS lo interprete como UTC
    const utcString = dateString && !dateString.endsWith('Z') && !dateString.includes('+') ? dateString + 'Z' : dateString;
    const date = new Date(utcString);
    return date.toLocaleString('es-VE', {
      timeZone: 'America/Caracas',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  
  useEffect(() => {
    if (ticket?.id && isOpen) dispatch(obtenerTicket(ticket.id));
  }, [dispatch, ticket?.id, isOpen]);
  
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);
  
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!mensaje.trim()) return;
    
    try {

      
      // Crear FormData para enviar mensaje con archivos
      const formData = new FormData();
      formData.append('contenido', mensaje.trim());
      formData.append('usuario_id', userData.id);
      formData.append('usuario_nombre', `${userData.nombre} ${userData.apellido}`.trim());
      formData.append('es_admin', isAdmin);
      
      // Agregar archivos si existen
      if (files.length > 0) {
        for (const fileItem of files) {
          if (fileItem.file) {
            formData.append('archivos', fileItem.file);
          }
        }
      }
      
      // Enviar con fetch directo porque necesitamos FormData
      const token = SecureStorage.getAccessToken();
      
      if (!token) {
        throw new Error('No hay token de autenticación');
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/tickets/${ticketActual.id}/mensajes`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );
      
      if (!response.ok) {
        throw new Error('Error al enviar mensaje');
      }
      
      const result = await response.json();

      setMensaje('');
      setFiles([]);
      
      // Recargar ticket completo para obtener mensajes actualizados
      const ticketActualizado = await dispatch(obtenerTicket(ticketActual.id)).unwrap();

    } catch (error) {
      console.error('❌ Error:', error);
      toast.error('Error al enviar mensaje');
    }
  };
  
  const handleMarcarDuplicado = async () => {
    if (!ticketOriginalId.trim()) {
      toast.error('Selecciona el ticket original');
      return;
    }
    setMarcandoDuplicado(true);
    try {
      await dispatch(marcarComoDuplicado({ ticketId: ticketActual.id, ticketOriginalId: ticketOriginalId })).unwrap();
      setShowDuplicadoModal(false);
      setTicketOriginalId('');
      // Recargar ticket
      dispatch(obtenerTicket(ticketActual.id));
    } catch (error) {
      console.error('Error al marcar como duplicado:', error);
    } finally {
      setMarcandoDuplicado(false);
    }
  };

  const getStatusColor = (estatus) => {
    const colors = { 'Abierto': 'primary', 'En Progreso': 'warning', 'Cerrado': 'success', 'Duplicado': 'secondary' };
    return colors[estatus] || 'secondary';
  };
  
  const getPriorityColor = (prioridad) => {
    const colors = { 'Urgente': 'danger', 'Alta': 'warning', 'Media': 'info', 'Baja': 'secondary' };
    return colors[prioridad] || 'secondary';
  };
  
  // Ver adjunto en nueva pestaña usando URL prefirmada (como en solicitudes)
  const handleViewAttachment = async (archivo) => {
    try {
      const ticketId = ticketActual.id;
      const nombreArchivo = archivo.nombre_archivo;
      
      // Usar helper que maneja autenticación correctamente (como en solicitudes)
      const response = await getUrlPrefirmadaTicket(ticketId, nombreArchivo, 3600);
      
      if (response.data && response.data.url) {
        // Abrir en nueva pestaña con la URL prefirmada
        window.open(response.data.url, '_blank');
      } else {
        throw new Error('No se recibió URL del archivo');
      }
    } catch (error) {
      console.error('❌ Error al ver archivo:', error);
      toast.error('Error al cargar el archivo');
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      <div className={`drawer-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}></div>
      <div className={`ticket-drawer ${isOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <h5>Detalle del Ticket</h5>
          <button type="button" className="btn btn-sm btn-icon" onClick={onClose} style={{ border: '1px solid #adb5bd', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
            <i className="ri-close-line fs-5 text-dark"></i>
          </button>
        </div>
        
        <div className="drawer-body">
          {loading || !ticketActual ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              <div className="ticket-info-section">
                <div className="d-flex align-items-center gap-2 mb-1">
                  {ticketActual.identificador && (
                    <span className="badge bg-light text-dark fw-medium" style={{fontSize: '11px'}}>{ticketActual.identificador}</span>
                  )}
                  <h6 className="mb-0">{ticketActual.titulo}</h6>
                </div>
                <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                  <Badge color={getStatusColor(ticketActual.estatus)}>{ticketActual.estatus}</Badge>
                  <Badge color={getPriorityColor(ticketActual.prioridad)}>{ticketActual.prioridad}</Badge>
                  <Badge color="secondary">{ticketActual.categoria}</Badge>
                  {isAdmin && ticketActual.estatus !== 'Duplicado' && ticketActual.estatus !== 'Cerrado' && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm rounded-pill ms-auto"
                      style={{ fontSize: '11px', padding: '2px 10px' }}
                      onClick={() => setShowDuplicadoModal(true)}
                      title="Marcar como duplicado de otro ticket"
                    >
                      <i className="ri-file-copy-line me-1"></i>
                      Duplicado
                    </button>
                  )}
                </div>
                {isAdmin && (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="text-muted fs-11">
                      <i className="ri-information-line me-1 align-middle"></i>
                      Cambiar estatus:
                    </span>
                    {ticketActual.estatus === 'Abierto' && (
                      <Button color="warning" size="sm" onClick={() => onStatusChange(ticketActual.id, 'En Progreso')} title="Cambiar estatus a En Progreso">
                        <i className="ri-play-circle-line me-1"></i>
                        En Progreso
                      </Button>
                    )}
                    {ticketActual.estatus === 'En Progreso' && (
                      <Button color="success" size="sm" onClick={() => onStatusChange(ticketActual.id, 'Cerrado')} title="Cambiar estatus a Cerrado">
                        <i className="ri-check-line me-1"></i>
                        Cerrar
                      </Button>
                    )}
                    {ticketActual.estatus === 'Cerrado' && (
                      <Button color="info" size="sm" onClick={() => onStatusChange(ticketActual.id, 'Abierto')} title="Reabrir este ticket">
                        <i className="ri-restart-line me-1"></i>
                        Reabrir
                      </Button>
                    )}
                    {ticketActual.estatus === 'Duplicado' && (
                      <Button color="info" size="sm" onClick={() => onStatusChange(ticketActual.id, 'Abierto')} title="Desmarcar como duplicado y reabrir">
                        <i className="ri-restart-line me-1"></i>
                        Reabrir
                      </Button>
                    )}
                  </div>
                )}
                
                {/* Alerta de ticket duplicado */}
                {ticketActual.duplicado_de && ticketActual.estatus === 'Duplicado' && (
                  <div className="alert alert-warning d-flex align-items-center gap-2 py-2 mb-3" role="alert">
                    <i className="ri-file-copy-line fs-5"></i>
                    <div>
                      <strong>Ticket Duplicado</strong>
                      <br />
                      <small>
                        Este ticket es duplicado del ticket:
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ms-1"
                          onClick={() => {
                            const ticketOrig = tickets.find(t => t.id === ticketActual.duplicado_de);
                            if (ticketOrig) {
                              onClose();
                              setTimeout(() => {
                                // Abrir el ticket original
                                const event = new CustomEvent('openTicket', { detail: ticketOrig });
                                window.dispatchEvent(event);
                              }, 300);
                            } else {
                              toast.info(`ID del ticket original: ${ticketActual.duplicado_de}`);
                            }
                          }}
                        >
                          {(() => {
                            const ticketOrig = tickets.find(t => t.id === ticketActual.duplicado_de);
                            return ticketOrig ? ticketOrig.titulo : ticketActual.duplicado_de;
                          })()}
                        </button>
                      </small>
                    </div>
                  </div>
                )}
                
                <div className="info-grid">
                  {/* Columna izquierda: Datos del usuario */}
                  <div className="info-grid-col">
                    <div className="info-item">
                      <div className="info-icon bg-primary-subtle">
                        <i className="ri-user-3-line"></i>
                      </div>
                      <div className="info-content">
                        <label>Usuario</label>
                        <span>{ticketActual.usuario_nombre}</span>
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-icon bg-info-subtle">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div className="info-content">
                        <label>Email</label>
                        <span>{ticketActual.usuario_email}</span>
                      </div>
                    </div>
                    {ticketActual.usuario_telefono && (
                      <div className="info-item">
                        <div className="info-icon bg-success-subtle">
                          <i className="ri-phone-line"></i>
                        </div>
                        <div className="info-content">
                          <label>Teléfono</label>
                          <span>{ticketActual.usuario_telefono}</span>
                        </div>
                      </div>
                    )}
                    {ticketActual.farmacia_id && (
                      <div className="info-item">
                        <div className="info-icon bg-warning-subtle">
                          <i className="ri-store-2-line"></i>
                        </div>
                        <div className="info-content">
                          <label>Farmacia</label>
                          <span>{ticketActual.farmacia_nombre || ticketActual.farmacia_id}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Columna derecha: Fechas y URL */}
                  <div className="info-grid-col">
                    <div className="info-item">
                      <div className="info-icon bg-secondary-subtle">
                        <i className="ri-calendar-line"></i>
                      </div>
                      <div className="info-content">
                        <label>Creado</label>
                        <span>{formatVenezuelaDate(ticketActual.creado)}</span>
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-icon bg-secondary-subtle">
                        <i className="ri-refresh-line"></i>
                      </div>
                      <div className="info-content">
                        <label>Actualizado</label>
                        <span>{formatVenezuelaDate(ticketActual.actualizado)}</span>
                      </div>
                    </div>
                    {ticketActual.url_actual && (
                      <div className="info-item">
                        <div className="info-icon bg-primary-subtle">
                          <i className="ri-links-line"></i>
                        </div>
                        <div className="info-content">
                          <label>Página de origen</label>
                          <a href={ticketActual.url_actual} target="_blank" rel="noopener noreferrer">
                            <i className="ri-external-link-line me-1"></i>
                            Abrir página
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="mt-3">
                  <Label>Descripción:</Label>
                  <div className="description-box">{ticketActual.descripcion}</div>
                </div>
                
                {ticketActual.adjuntos && ticketActual.adjuntos.length > 0 && (
                  <div className="mt-3">
                    <Label>Archivos Adjuntos ({ticketActual.adjuntos.length}):</Label>
                    <div className="attached-files">
                      {ticketActual.adjuntos.map((archivo) => (
                        <div key={archivo.id} className="attached-file">
                          <i className="ri-attachment-line me-2"></i>
                          <button
                            type="button"
                            className="file-link-button"
                            onClick={() => handleViewAttachment(archivo)}
                          >
                            {archivo.nombre_original}
                          </button>
                          <Badge color="secondary" className="ms-2">
                            {(archivo.tamaño / 1024).toFixed(1)} KB
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="messages-section">
                <h6>Conversación ({ticketActual.mensajes?.length || 0})</h6>
                <div className="messages-container">
                  {ticketActual.mensajes?.map((msg) => (
                    <div key={msg.id} className={`message ${msg.es_admin ? 'admin' : 'user'}`}>
                      <div className="message-header">
                        <strong>{msg.usuario_nombre}</strong>
                        <small>{formatVenezuelaDate(msg.creado)}</small>
                      </div>
                      <div className="message-content">{msg.contenido}</div>
                      {msg.adjuntos && msg.adjuntos.length > 0 && (
                        <div className="message-attachments mt-2">
                          {msg.adjuntos.map((archivo) => (
                            <div key={archivo.id} className="message-attachment-item">
                              <i className="ri-attachment-line me-1"></i>
                              <button
                                type="button"
                                className="file-link-button"
                                onClick={() => handleViewAttachment(archivo)}
                              >
                                {archivo.nombre_original}
                              </button>
                              <Badge color="secondary" className="ms-1" style={{fontSize: '0.7rem'}}>
                                {(archivo.tamaño / 1024).toFixed(1)} KB
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {ticketActual.estatus === 'En Progreso' && (
                  <form onSubmit={handleSendMessage} className="mt-3">
                    <Label>Responder:</Label>
                    <Input
                      type="textarea"
                      rows="3"
                      placeholder="Escribe tu respuesta..."
                      value={mensaje}
                      onChange={(e) => setMensaje(e.target.value)}
                      disabled={enviandoMensaje}
                      className="mb-2"
                    />
                    
                    <Label className="mt-2">Archivos adjuntos (opcional):</Label>
                    <FilePond
                      files={files}
                      onupdatefiles={setFiles}
                      allowMultiple={true}
                      maxFiles={2}
                      name="files"
                      labelIdle='<i class="ri-upload-cloud-line fs-20 mb-1"></i><br/>Arrastra o <span class="filepond--label-action text-primary">selecciona</span> archivos'
                      className="filepond-small"
                      allowFileEncode={true}
                      acceptedFileTypes={["image/*", "application/pdf"]}
                      labelFileTypeNotAllowed="Solo se permiten imágenes y PDF"
                      fileValidateTypeLabelExpectedTypes="Seleccione una imagen (JPG, PNG, WEBP) o PDF"
                      maxFileSize="5MB"
                      credits={false}
                      disabled={enviandoMensaje}
                    />
                    
                    <Button color="primary" type="submit" className="mt-2" disabled={!mensaje.trim() || enviandoMensaje}>
                      {enviandoMensaje ? (
                        <>
                          <Spinner size="sm" className="me-1" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line me-1"></i>
                          Enviar
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {/* Modal para marcar como duplicado */}
      <Modal isOpen={showDuplicadoModal} toggle={() => setShowDuplicadoModal(false)} centered>
        <ModalHeader toggle={() => setShowDuplicadoModal(false)}>
          <i className="ri-file-copy-line me-2"></i>
          Marcar como Duplicado
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            Busca y selecciona el ticket original del cual este es duplicado:
          </p>
          <Label>Buscar ticket</Label>
          <div className="search-box mb-2">
            <Input
              type="text"
              className="form-control search"
              placeholder="Buscar por título, usuario o email..."
              value={busquedaDuplicado}
              onChange={(e) => setBusquedaDuplicado(e.target.value)}
              autoComplete="off"
            />
            <i className="ri-search-line search-icon" style={{ pointerEvents: "none" }}></i>
          </div>
          
          {/* Ticket seleccionado */}
          {ticketOriginalId && (() => {
            const selected = tickets.find(t => t.id === ticketOriginalId);
            return selected ? (
              <div className="d-flex align-items-center justify-content-between p-2 mb-2 rounded" style={{ backgroundColor: '#e8f5e9', border: '1px solid #a5d6a7' }}>
                <div>
                  <strong className="d-block">{selected.identificador && `${selected.identificador} - `}{selected.titulo}</strong>
                  <small className="text-muted">{selected.usuario_nombre} - {selected.estatus}</small>
                </div>
                <Button color="link" size="sm" className="text-danger p-0" onClick={() => setTicketOriginalId('')}>
                  <i className="ri-close-circle-line fs-5"></i>
                </Button>
              </div>
            ) : null;
          })()}
          
          {/* Lista filtrada */}
          <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
            {tickets
              .filter(t => {
                if (t.id === ticketActual?.id || t.estatus === 'Duplicado') return false;
                if (!busquedaDuplicado.trim()) return true;
                const search = busquedaDuplicado.toLowerCase();
                return (
                  t.identificador?.toLowerCase().includes(search) ||
                  t.titulo?.toLowerCase().includes(search) ||
                  t.usuario_nombre?.toLowerCase().includes(search) ||
                  t.usuario_email?.toLowerCase().includes(search)
                );
              })
              .map(t => (
                <div
                  key={t.id}
                  className={`p-2 border-bottom ${ticketOriginalId === t.id ? 'bg-primary-subtle' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setTicketOriginalId(t.id)}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <strong className="d-block" style={{ fontSize: '13px' }}>
                        {t.identificador && <span className="badge bg-light text-dark me-1" style={{fontSize: '10px'}}>{t.identificador}</span>}
                        {t.titulo}
                      </strong>
                      <small className="text-muted">{t.usuario_nombre}</small>
                    </div>
                    <span className={`badge badge-soft-${t.estatus === 'Abierto' ? 'primary' : t.estatus === 'En Progreso' ? 'warning' : 'success'}`} style={{ fontSize: '10px' }}>
                      {t.estatus}
                    </span>
                  </div>
                </div>
              ))
            }
            {tickets.filter(t => {
              if (t.id === ticketActual?.id || t.estatus === 'Duplicado') return false;
              if (!busquedaDuplicado.trim()) return true;
              const search = busquedaDuplicado.toLowerCase();
              return t.identificador?.toLowerCase().includes(search) || t.titulo?.toLowerCase().includes(search) || t.usuario_nombre?.toLowerCase().includes(search) || t.usuario_email?.toLowerCase().includes(search);
            }).length === 0 && (
              <div className="text-center text-muted py-3">
                <i className="ri-inbox-line fs-4 d-block mb-1"></i>
                No se encontraron tickets
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => { setShowDuplicadoModal(false); setTicketOriginalId(''); setBusquedaDuplicado(''); }} disabled={marcandoDuplicado}>
            Cancelar
          </Button>
          <Button color="warning" onClick={handleMarcarDuplicado} disabled={!ticketOriginalId || marcandoDuplicado}>
            {marcandoDuplicado ? (
              <><Spinner size="sm" className="me-1" />Procesando...</>
            ) : (
              <><i className="ri-file-copy-line me-1"></i>Marcar como Duplicado</>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TicketDrawer;
